import R1answerKey from '../data/Reading1/R1answerKey.json';

/**
 * GradingEngine: Dynamically evaluates student answers against the official IELTS Academic Reading answer key
 * and converts raw correct scores to official band scores using the official conversion table.
 */
export const GradingEngine = {
  /**
   * Normalizes an answer string by:
   * 1. Trimming whitespace and converting to uppercase.
   * 2. Extracting option letters from answers with descriptions (e.g. "C. receive feedback..." -> "C" or "receive feedback...").
   * 3. Mapping semantic equivalents across TRUE / FALSE / NOT GIVEN (and YES / NO / NOT GIVEN).
   * 4. Supporting interchangeable index mappings (A/B/C) when evaluating against TFNG / YNNG question types.
   */
  normalizeAnswer(val, questionType = '') {
    if (val === undefined || val === null) return '';
    let str = String(val).trim().toUpperCase();

    // Check if question is a TFNG or YNNG type
    const isTFNG = questionType.toUpperCase().includes('TFNG') || 
                   questionType.toUpperCase().includes('TRUE') || 
                   questionType.toUpperCase().includes('FALSE');
    const isYNNG = questionType.toUpperCase().includes('YNNG') || 
                   questionType.toUpperCase().includes('YES') || 
                   questionType.toUpperCase().includes('NO');

    // Semantic Mapping for True/False/Not Given and Yes/No/Not Given
    if (isTFNG) {
      if (str === 'TRUE' || str === 'T' || str === 'A' || str.startsWith('TRUE') || str.startsWith('A.')) return 'TRUE';
      if (str === 'FALSE' || str === 'F' || str === 'B' || str.startsWith('FALSE') || str.startsWith('B.')) return 'FALSE';
      if (str === 'NOT GIVEN' || str === 'NOTGIVEN' || str === 'NG' || str === 'C' || str.startsWith('NOT GIVEN') || str.startsWith('C.')) return 'NOT GIVEN';
    }

    if (isYNNG) {
      if (str === 'YES' || str === 'Y' || str === 'A' || str.startsWith('YES') || str.startsWith('A.')) return 'YES';
      if (str === 'NO' || str === 'N' || str === 'B' || str.startsWith('NO') || str.startsWith('B.')) return 'NO';
      if (str === 'NOT GIVEN' || str === 'NOTGIVEN' || str === 'NG' || str === 'C' || str.startsWith('NOT GIVEN') || str.startsWith('C.')) return 'NOT GIVEN';
    }

    // General Semantic equivalents if no specific type is passed
    if (str === 'TRUE' || str === 'T') return 'TRUE';
    if (str === 'FALSE' || str === 'F') return 'FALSE';
    if (str === 'YES' || str === 'Y') return 'YES';
    if (str === 'NO' || str === 'N') return 'NO';
    if (str === 'NOT GIVEN' || str === 'NOTGIVEN' || str === 'NG') return 'NOT GIVEN';

    // Extract leading option letter if student answer starts with "A.", "B - ", "C)", etc.
    const optionMatch = str.match(/^([A-J])[.\-):\s]/);
    if (optionMatch) {
      return optionMatch[1];
    }

    // If single letter choice A-J
    if (/^[A-J]$/.test(str)) {
      return str;
    }

    // Clean inline punctuation and redundant spaces for text completion
    return str.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '').replace(/\s+/g, ' ').trim();
  },

  /**
   * Compares a student answer against an official key answer with intelligent semantic fallback.
   */
  isAnswerMatch(studentAnsRaw, officialAnsRaw, questionType = '') {
    if (studentAnsRaw === undefined || studentAnsRaw === null || String(studentAnsRaw).trim() === '') {
      return false;
    }

    const normStudent = this.normalizeAnswer(studentAnsRaw, questionType);
    const normOfficial = this.normalizeAnswer(officialAnsRaw, questionType);

    // Direct normalized match
    if (normStudent === normOfficial) {
      return true;
    }

    // Secondary Check: If official answer is a single letter (e.g. "C") and student answer starts with "C."
    const cleanStudentRaw = String(studentAnsRaw).trim().toUpperCase();
    const cleanOfficialRaw = String(officialAnsRaw).trim().toUpperCase();

    if (/^[A-J]$/.test(cleanOfficialRaw)) {
      if (cleanStudentRaw.startsWith(cleanOfficialRaw + '.') || 
          cleanStudentRaw.startsWith(cleanOfficialRaw + ' -') || 
          cleanStudentRaw.startsWith(cleanOfficialRaw + ')') ||
          cleanStudentRaw.startsWith(cleanOfficialRaw + ' ')) {
        return true;
      }
    }

    // Secondary Check: If student answer is "C" and official answer is "C. Option text..."
    if (/^[A-J]$/.test(cleanStudentRaw)) {
      if (cleanOfficialRaw.startsWith(cleanStudentRaw + '.') || 
          cleanOfficialRaw.startsWith(cleanStudentRaw + ' -') || 
          cleanOfficialRaw.startsWith(cleanStudentRaw + ')') ||
          cleanOfficialRaw.startsWith(cleanStudentRaw + ' ')) {
        return true;
      }
    }

    return false;
  },

  calculateScore(studentAnswers = {}, customKeyMap = null, questionMetadataMap = {}) {
    let correctCount = 0;
    let incorrectCount = 0;
    let unansweredCount = 0;
    const results = [];
    
    // Retrieve answer key object (supporting injected or default R1 answer key)
    const keyMap = customKeyMap || R1answerKey.answers || R1answerKey;
    const totalQuestions = Object.keys(keyMap).length || 40;

    Object.keys(keyMap).forEach((qId) => {
      const officialAnswer = keyMap[qId];
      const rawStudentAns = studentAnswers[qId];
      const qMeta = questionMetadataMap[qId] || {};
      const qType = qMeta.type || qMeta.questionType || '';

      const isCorrect = this.isAnswerMatch(rawStudentAns, officialAnswer, qType);

      if (rawStudentAns === undefined || rawStudentAns === null || String(rawStudentAns).trim() === '') {
        unansweredCount++;
      } else if (isCorrect) {
        correctCount++;
      } else {
        incorrectCount++;
      }

      results.push({
        questionId: qId,
        studentAnswer: rawStudentAns !== undefined && rawStudentAns !== null ? String(rawStudentAns) : '',
        correctAnswer: officialAnswer,
        isCorrect: isCorrect
      });
    });

    // Convert raw correct score to official IELTS Academic Reading Band Score based on the conversion chart
    const band = this.convertToIELTSBand(correctCount);

    return {
      rawScore: correctCount,
      maxScore: totalQuestions,
      correctCount,
      incorrectCount,
      unansweredCount,
      estimatedBand: `Band ${band}`,
      bandValue: band,
      results
    };
  },

  /**
   * Official IELTS Reading (Academic) Conversion Mapping extracted from specification chart:
   * 39-40 -> 9.0
   * 37-38 -> 8.5
   * 35-36 -> 8.0
   * 33-34 -> 7.5
   * 30-32 -> 7.0
   * 27-29 -> 6.5
   * 23-26 -> 6.0
   * 19-22 -> 5.5
   * 15-18 -> 5.0
   * 13-14 -> 4.5
   * 10-12 -> 4.0
   * 8-9   -> 3.5
   * 6-7   -> 3.0
   * 4-5   -> 2.5
   */
  convertToIELTSBand(score) {
    if (score >= 39) return 9.0;
    if (score >= 37) return 8.5;
    if (score >= 35) return 8.0;
    if (score >= 33) return 7.5;
    if (score >= 30) return 7.0;
    if (score >= 27) return 6.5;
    if (score >= 23) return 6.0;
    if (score >= 19) return 5.5;
    if (score >= 15) return 5.0;
    if (score >= 13) return 4.5;
    if (score >= 10) return 4.0;
    if (score >= 8)  return 3.5;
    if (score >= 6)  return 3.0;
    if (score >= 4)  return 2.5;
   if (score <= 0) return 2.0; 
  }
};

export default GradingEngine;